import Text from '@/common/components/atom/text';
import useIsNewAppShell from '@/common/hooks/useIsNewAppShell';
import { useRouter } from 'next/router';
import { areaFilterItems, hasUserQuery, listingNames, parentListingPath, specialtyWithCity } from '../../functions/areaPages';
import { useSearch } from '../../hooks/useSearch';

const faNumber = (n: number) => n.toLocaleString('fa-IR');

/**
 * DR-43, at the BOTTOM of the page, server-rendered, nothing above the results (owner, 2026-10-27: no new row, the first card
 * must not move). People choose a neighbourhood in the ordinary «محله» filter (the chip row, the «فیلترها» sheet, the desktop sidebar); this
 * block is the plain `<a href>` list crawlers (and a no-JS reader) get, built from the same filter items.
 * - Parent listing (clean URL): «{تخصص} در محله‌های {شهر}» + the neighbourhood links.
 * - Area page: the link up to the whole specialty and the OpenStreetMap credit the boundaries' licence (ODbL) asks for.
 *   (The neighbouring neighbourhoods / other specialties links are inside the SEO box: view/seoBox.)
 */
export const AreaLinks = () => {
  const { area, filters, seoInfo } = useSearch();
  const { asPath } = useRouter();
  const isApp = useIsNewAppShell();

  if (isApp) return null;

  if (!area) {
    if (hasUserQuery(asPath)) return null;
    const links = areaFilterItems(filters);
    if (links.length === 0) return null;
    const names = listingNames((seoInfo as any)?.jsonld, false);
    return (
      <section data-testid="area-links" className="flex flex-col pb-5 space-y-3" aria-label="محله‌ها">
        <Text as="h2" fontWeight="bold" fontSize="sm">
          {names ? `${names.group} در محله‌های ${names.city}` : 'محله‌ها'}
        </Text>
        <ul className="flex flex-wrap gap-2">
          {links.map(link => (
            <li key={link.value}>
              <a
                // the page's own tracking parameters (utm_*…) ride on the filter hrefs; a crawlable link is the clean path
                href={link.href.split('?')[0]}
                className="inline-flex items-center min-h-[44px] px-4 text-sm bg-white border rounded-full border-slate-200 text-slate-700 hover:border-primary"
              >
                {link.title}
                {link.count !== undefined && <span className="text-slate-400"> ({faNumber(link.count)})</span>}
              </a>
            </li>
          ))}
        </ul>
      </section>
    );
  }

  const names = listingNames((seoInfo as any)?.jsonld, true, area.name_fa);
  return (
    <section data-testid="area-page-footer" className="flex flex-col pb-5 space-y-2">
      <a href={parentListingPath(asPath)} className="text-sm font-medium text-primary">
        {/* the CMS crumb is sometimes already «پوست و مو در تهران» — don't say the city twice */}
        {names ? `همهٔ پزشکان ${specialtyWithCity(names.group, names.city).withCity}` : 'همهٔ پزشکان این تخصص در شهر'}
      </a>
      <Text fontSize="xs" className="text-slate-500">
        مرز محله‌ها: © OpenStreetMap contributors
      </Text>
    </section>
  );
};

export default AreaLinks;
